<script setup lang='ts'>
import HeaderAPI from "~/services/header";

const { t } = useI18n();
const appConfig = useRuntimeConfig().public;
const sessionStorageKey = `${appConfig.APP_CUSTOMER.name_short}-links`;

const { data: headerLinks, pending } = useAsyncData(
  "header-links",
  HeaderAPI.load,
  {
    getCachedData: (key) => {
      // First check Nuxt's payload/static data
      const payloadData = useNuxtApp().payload.data[key] || useNuxtApp().static.data[key];
      if (payloadData)
        return payloadData;

      // Then check sessionStorage to prevent refetching on client-side navigation
      if (import.meta.client) {
        const cached = useSessionStorage<IHeaderLink[]>(sessionStorageKey, []);
        if (cached.value && cached.value.length > 0)
          return cached.value;
      }

      return undefined;
    },
  },
);

const containerRef = ref<HTMLElement | null>(null);
const visibleLinks = ref<IHeaderLink[]>([]);
const invisibleLinks = ref<IHeaderLink[]>([]);

const linkWidthCache = ref(new Map<string, number>());

const measureTextWidth = (text: string): number => {
  if (!import.meta.client)
    return 0;

  const span = document.createElement("span");
  span.style.visibility = "hidden";
  span.style.position = "absolute";
  span.style.fontWeight = "500";
  span.style.whiteSpace = "nowrap";
  span.style.fontSize = "1rem";
  span.style.lineHeight = "1.25rem";
  span.textContent = text;
  document.body.appendChild(span);
  const width = span.offsetWidth;
  document.body.removeChild(span);
  return width;
};

const getLinkWidth = (link: IHeaderLink): number => {
  const displayTitle = link.id === "following" ? t("labels.following") : link.title;

  if (linkWidthCache.value.has(displayTitle))
    return linkWidthCache.value.get(displayTitle)!;

  // px-6 = 24px on each side = 48px total padding
  const textWidth = measureTextWidth(displayTitle);
  const width = Math.min(textWidth, 144) + 48;
  linkWidthCache.value.set(displayTitle, width);
  return width;
};

const calculateVisibleLinks = () => {
  if (!headerLinks.value?.length || !import.meta.client || !containerRef.value)
    return;

  const containerWidth = containerRef.value.offsetWidth;
  // Reserve space for invisible links button (approximately 48px) and some margin
  const maxWidth = containerWidth - 100;

  let totalWidth = 0;
  const newVisibleLinks: IHeaderLink[] = [];
  const newInvisibleLinks: IHeaderLink[] = [];

  for (const link of headerLinks.value) {
    const linkWidth = getLinkWidth(link);
    totalWidth += linkWidth;

    if (totalWidth <= maxWidth)
      newVisibleLinks.push(link);
    else
      newInvisibleLinks.push(link);
  }

  visibleLinks.value = newVisibleLinks;
  invisibleLinks.value = newInvisibleLinks;
};

let resizeTimeoutId: ReturnType<typeof setTimeout> | null = null;
let resizeObserver: ResizeObserver | null = null;

const debouncedResize = () => {
  if (resizeTimeoutId)
    clearTimeout(resizeTimeoutId);

  resizeTimeoutId = setTimeout(() => {
    calculateVisibleLinks();
  }, 150);
};

watch(() => headerLinks.value, () => {
  if (import.meta.client && containerRef.value) {
    linkWidthCache.value.clear();
    nextTick(() => {
      calculateVisibleLinks();
    });
  }
}, { immediate: false, deep: true });

onMounted(() => {
  if (import.meta.client && containerRef.value) {
    // Use ResizeObserver for better performance than window resize
    resizeObserver = new ResizeObserver(debouncedResize);
    resizeObserver.observe(containerRef.value as unknown as Element);

    // Also listen to window resize as fallback
    window.addEventListener("resize", debouncedResize);

    // Initial calculation
    nextTick(() => {
      calculateVisibleLinks();
    });
  }
});

onBeforeUnmount(() => {
  if (import.meta.client) {
    if (resizeObserver && containerRef.value) {
      resizeObserver.unobserve(containerRef.value as unknown as Element);
      resizeObserver.disconnect();
      resizeObserver = null;
    }

    window.removeEventListener("resize", debouncedResize);

    if (resizeTimeoutId) {
      clearTimeout(resizeTimeoutId);
      resizeTimeoutId = null;
    }

    linkWidthCache.value.clear();
  }
});
</script>

<template>
  <div
    ref="containerRef"
    class="flex items-center py-6 border-b border-transparent"
  >
    <div v-if="pending" class="flex items-center px-6 overflow-hidden w-full">
      <div class="space-x-6 flex flex-row">
        <div v-for="i in 12" :key="i" class="h-6 w-28 flex-none rounded-md skeletonLoading" />
      </div>
    </div>
    <template v-else-if="headerLinks?.length">
      <!-- Mobile: Show all links in horizontal scrollable container -->
      <div class="flex items-center overflow-x-auto overflow-y-hidden snap-x no-scrollbar lg:hidden w-full">
        <AppHeaderVisibleLinks :links="headerLinks" />
      </div>
      <!-- Desktop: Show visible/invisible links with dropdown -->
      <div class="hidden lg:flex items-center">
        <AppHeaderVisibleLinks :links="visibleLinks" />
        <AppHeaderInvisibleLinks :links="invisibleLinks" />
      </div>
    </template>
  </div>
</template>
